import React, { useState } from "react";
import { Rich } from "../rich";
import * as styles from "./FAQ.module.scss";
import type { RichText } from "@shared/arrays/pop-ups-shared";

export interface FaqItem {
  q: string;
  a: RichText;
  /** Plain-text copy of the answer, for the schema.org itemProp. */
  plain: string;
}

export interface FAQProps {
  title: string;
  items: FaqItem[];
}

/**
 * The questions the export already had, marked up as FAQPage so they can
 * surface as rich results — Tilda rendered them as a plain accordion.
 */
export function FAQ({ title, items }: FAQProps) {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section
      className={styles.section}
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <div className={styles.inner}>
        <h2 className={styles.title}>{title}</h2>

        <ul className={styles.list}>
          {items.map((item, i) => (
            <li
              key={item.q}
              className={`${styles.item} ${open === i ? styles.itemOpen : ""}`}
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
            >
              <button
                type="button"
                className={styles.question}
                aria-expanded={open === i}
                onClick={() => setOpen(open === i ? null : i)}
              >
                <span itemProp="name">{item.q}</span>
                <svg
                  className={styles.chevron}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 8L10 13L15 8"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <div
                className={styles.answer}
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
              >
                {/* The visible answer keeps its inline links; the schema value
                    is the same sentence as plain text. */}
                <meta itemProp="text" content={item.plain} />
                <p>
                  <Rich parts={item.a} />
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export default FAQ;
